import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, MenuItem } from '@mui/material';
// components
import { CustomAvatar } from '@raas/shared/src/components/minimals/custom-avatar';
import { useSnackbar } from '@raas/shared/src/components/minimals/snackbar';
import MenuPopover from '@raas/shared/src/components/minimals/menu-popover';
import { IconButtonAnimate } from '@raas/shared/src/components/minimals/animate';

// routes
import { PATH_AUTH, PATH_DASHBOARD } from '@raas/shared/src/routes/paths';
// auth
import { useAuthContext } from '@raas/shared/src/auth/useAuthContext';
// keycloak
import { useKeycloak } from '@react-keycloak/web';

export default function AccountPopover() {
  const navigate = useNavigate();

  const { user, logout } = useAuthContext();

  const { keycloak } = useKeycloak();

  const { enqueueSnackbar } = useSnackbar();

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleChangePassword = async () => {
    handleClosePopover();
    navigate(PATH_DASHBOARD.user.changePassword);
  };

  function isKontrolUser(): boolean {
    return user.userType === 'Kontrol';
  }

  const handleLogout = async () => {
    try {
      keycloak.clearToken();
      logout();
      navigate(PATH_AUTH.login, { replace: true });
      handleClosePopover();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  return (
    <>
      <IconButtonAnimate
        data-cy="account-menu-button"
        aria-label="account menu"
        onClick={handleOpenPopover}
        sx={{
          p: 0,
          ...(openPopover && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <CustomAvatar alt={user?.displayName} name={user?.displayName} />
      </IconButtonAnimate>

      <MenuPopover open={openPopover} onClose={handleClosePopover} sx={{ width: 200, p: 0 }}>
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.displayName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />
        <MenuItem
          data-cy="change-password-menu-item"
          onClick={handleChangePassword}
          sx={{ m: 1 }}
          disabled={!isKontrolUser()}
        >
          Change Password
        </MenuItem>
        <Divider sx={{ borderStyle: 'dashed' }} />
        <MenuItem
          data-cy="profile-menu-item"
          onClick={() => navigate(PATH_DASHBOARD.user.profile)}
          sx={{ m: 1 }}
        >
          Profile
        </MenuItem>

        <Divider sx={{ borderStyle: 'dashed' }} />
        <MenuItem data-cy="logout-menu-item" onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
      </MenuPopover>
    </>
  );
}
